import { useEffect, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { Icon } from '../icons/Icon.js';

type ModalSize = 'sm' | 'md' | 'lg' | 'xl';

type Props = {
  open: boolean;
  onClose: () => void;
  title?: ReactNode;
  desc?: ReactNode;
  size?: ModalSize;
  /** 强制隐藏 X（用于必须做选择的 modal） */
  hideClose?: boolean;
  /** 点遮罩是否关闭，默认 true */
  closeOnOverlay?: boolean;
  children: ReactNode;
  footer?: ReactNode;
};

const WIDTH: Record<ModalSize, number> = { sm: 480, md: 560, lg: 720, xl: 920 };

export function Modal({
  open, onClose, title, desc, size = 'md',
  hideClose, closeOnOverlay = true,
  children, footer,
}: Props) {
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [open, onClose]);

  if (!open) return null;
  return createPortal(
    <div
      className="overlay"
      onClick={(e) => {
        if (closeOnOverlay && e.target === e.currentTarget) onClose();
      }}
    >
      <div
        className="modal"
        style={{ width: WIDTH[size] }}
        role="dialog"
        aria-modal="true"
        aria-label={typeof title === 'string' ? title : undefined}
      >
        {(title || !hideClose) && (
          <div className="modal-head">
            <div className="modal-head-text">
              {title && <div className="modal-title">{title}</div>}
              {desc && <div className="modal-sub">{desc}</div>}
            </div>
            {!hideClose && (
              <button type="button" className="icon-btn" onClick={onClose} title="关闭">
                <Icon name="close" size={16} />
              </button>
            )}
          </div>
        )}
        <div className="modal-body">{children}</div>
        {footer && <div className="modal-foot">{footer}</div>}
      </div>
    </div>,
    document.body,
  );
}
